import React from 'react';
import './ErrorBoundary.css';

/* ─────────────────────────────────────────────
   Error reporter — swap the body for Sentry,
   LogRocket, or your own endpoint.
   Called once per caught error.
───────────────────────────────────────────── */
function reportError(error, errorInfo) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, errorInfo);
    return;
  }

  // ── Option A: Sentry (recommended) ──────────
  // import * as Sentry from '@sentry/react';
  // Sentry.captureException(error, { extra: errorInfo });

  // ── Option B: Your own backend endpoint ─────
  try {
    fetch('/api/errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message:    error?.message,
        stack:      error?.stack,
        component:  errorInfo?.componentStack,
        url:        window.location.href,
        userAgent:  navigator.userAgent,
        timestamp:  new Date().toISOString(),
      }),
      // Best-effort — don't await, don't block
      keepalive: true,
    }).catch(() => {});
  } catch { /* never throw inside an error boundary */ }
}

/* ─────────────────────────────────────────────
   Illustration — keeps the page human
───────────────────────────────────────────── */
const BrokenIcon = () => (
  <svg
    className="eb__illustration"
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Hanger */}
    <path d="M60 20 C60 20 60 30 72 36 L100 56 C106 60 104 70 96 70 L24 70 C16 70 14 60 20 56 L48 36 C60 30 60 20 60 20Z"
      stroke="#e5e0d8" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="60" cy="17" r="5" stroke="#e5e0d8" strokeWidth="2.5" fill="none"/>
    {/* Crack lines */}
    <path d="M52 46 L58 52 L54 58" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M68 44 L64 50 L70 54" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Stars */}
    <circle cx="30" cy="90" r="2" fill="#e5e0d8" opacity="0.6"/>
    <circle cx="90" cy="88" r="1.5" fill="#e5e0d8" opacity="0.4"/>
    <circle cx="60" cy="98" r="2.5" fill="#e5e0d8" opacity="0.5"/>
  </svg>
);

/* ─────────────────────────────────────────────
   ErrorBoundary class component
   (must be a class — hooks can't catch errors)
───────────────────────────────────────────── */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError:   false,
      error:      null,
      errorInfo:  null,
      resetKey:   0,     // increment to force remount of children
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    reportError(error, errorInfo);
  }

  /* Soft reset — clear state, re-render children */
  handleSoftReset = () => {
    this.setState(prev => ({
      hasError:  false,
      error:     null,
      errorInfo: null,
      resetKey:  prev.resetKey + 1,
    }));
  };

  /* Hard reload — clears ALL in-memory and context state */
  handleHardReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      const isDev = process.env.NODE_ENV === 'development';
      const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'support@theluviajewels.com';

      return (
        <div className="eb" role="alert" aria-live="assertive">
          <div className="eb__card">
            <BrokenIcon />

            <h1 className="eb__title">Something didn't load right</h1>

            <p className="eb__message">
              We're sorry — an unexpected error interrupted your session.
              Your cart and order details are safe.
            </p>

            {/* Dev-only stack trace */}
            {isDev && error && (
              <details className="eb__details">
                <summary className="eb__details-summary">
                  Error details <span className="eb__details-badge">DEV</span>
                </summary>
                <pre className="eb__stack">
                  {error.toString()}
                  {errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="eb__actions">
              {/* Primary: soft reset — re-renders children */}
              <button
                type="button"
                className="eb__btn eb__btn--primary"
                onClick={this.handleSoftReset}
              >
                Try Again
              </button>

              {/* Secondary: hard reload — clears all state */}
              <button
                type="button"
                className="eb__btn eb__btn--secondary"
                onClick={this.handleHardReload}
              >
                Reload Page
              </button>

              {/* Safe home link — plain <a>, not <Link>, in case router is broken */}
              <a href="/" className="eb__btn eb__btn--ghost">
                Go to Homepage
              </a>
            </div>

            {/* Support — critical if this happens during checkout */}
            <p className="eb__support">
              If this keeps happening or you were mid-checkout,{' '}
              <a
                href={`mailto:${supportEmail}?subject=App error&body=Page: ${encodeURIComponent(window.location.href)}`}
                className="eb__support-link"
              >
                contact our support team
              </a>
              {' '}— we'll make it right.
            </p>
          </div>
        </div>
      );
    }

    // Pass resetKey so children fully remount on soft reset
    return (
      <React.Fragment key={this.state.resetKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

export default ErrorBoundary;
